// src/services/web3service.js
// GR33D-SIMPLIFIED-V2: [2025-02-27] - Service Web3 stable et robuste

import { ethers } from 'ethers';
import { 
    PROXY_ADDRESS, 
    CONTRACT_ABI, 
    NETWORK_CONFIG,
    UNISWAP_PAIR_ADDRESS,
    UNISWAP_ROUTER,
    WETH_ADDRESS,
    ERROR_MESSAGES
} from '../constants/contract';
import Logger from '../utils/logger-adapter';

class Web3Service {
    constructor() {
    // Singleton pattern
    if (Web3Service._instance) {
        return Web3Service._instance;
    }
    
    // Initialisation des états
    this.provider = null;
    this.contract = null;
    this.signer = null;
    this.account = null;
    this.chainId = null;
    this.pairContract = null;
    this.initialized = false;
    this.lastError = null;
    
    // Stockage pour retry
    this._connectAttempts = 0;
    this._retryTimeout = null;
    
    // Nouvelle propriété pour gérer les RPC alternatifs
    this.rpcStatus = {
        current: 0,  // Index du RPC actuel
        failures: new Map(), // Compteur d'échecs par RPC
        lastSwitch: Date.now()
    };
    
    // Mémoriser l'instance
    Web3Service._instance = this;
}

    /**
     * Initialisation du provider, signer et contrat
     * @returns {Promise<boolean>} Succès ou échec
     */
    async initialize() {
        try {
            if (this.initialized) return true;
            Logger.log("web3", "Initializing web3service");
            
            // Initialisation du provider
            if (window.ethereum) {
                this.provider = new ethers.providers.Web3Provider(window.ethereum);
                Logger.log("web3", "Provider initialized");
                
                // Récupération du réseau
                const network = await this.provider.getNetwork();
                this.chainId = network.chainId;
                Logger.log("web3", "Network detected", { chainId: this.chainId, name: network.name });
                
                // Vérification du compte connecté
                const accounts = await this.provider.listAccounts();
                
                if (accounts && accounts.length > 0) {
                    this.account = accounts[0];
                    this.signer = this.provider.getSigner();
                    
                    // Initialisation du contrat
                    this.contract = new ethers.Contract(
                        PROXY_ADDRESS,
                        CONTRACT_ABI,
                        this.signer
                    );
                    
                    Logger.log("web3", "Contract and account initialized", { account: this.account });
                    this.initialized = true;
                    
                    // Initialisation des contrats auxiliaires
                    this.initPairContract();
                    
                    return true;
                } else {
                    Logger.log("web3", "No accounts found, need explicit connection");
                    this.initialized = false;
                    return false;
                }
            } else {
                Logger.warn("web3", "No ethereum provider found");
                this.initialized = false;
                return false;
            }
        } catch (error) {
            this.lastError = error;
            Logger.error(error, { context: 'initialize' });
            this.initialized = false;
            return false;
        }
    }

    /**
     * Connexion du wallet
     * @returns {Promise<{success: boolean, account: string|null}>}
     */
    async connectWallet() {
        try {
            // Vérification et initialisation
            if (!window.ethereum) {
                throw new Error('MetaMask not installed');
            }
            
            Logger.log("web3", "Connecting wallet");
            this._connectAttempts++;
            
            // Demande de connexion
            const accounts = await window.ethereum.request({ 
                method: 'eth_requestAccounts' 
            });
            
            if (!accounts || accounts.length === 0) {
                throw new Error('No accounts found');
            }
            
            // Mise à jour de l'état
            this.provider = new ethers.providers.Web3Provider(window.ethereum);
            this.signer = this.provider.getSigner();
            this.account = accounts[0];
            
            // Initialisation du contrat
            this.contract = new ethers.Contract(
                PROXY_ADDRESS,
                CONTRACT_ABI,
                this.signer
            );
            
            // Récupération du réseau
            const network = await this.provider.getNetwork();
            this.chainId = network.chainId;
            
            // Vérification du réseau
            if (this.chainId !== NETWORK_CONFIG.CHAIN_ID) {
                Logger.warn("web3", "Wrong network", { 
                    current: this.chainId, 
                    required: NETWORK_CONFIG.CHAIN_ID 
                });
                
                // On ne throw pas ici pour éviter de bloquer l'app
            }
            
            // Initialisation des contrats auxiliaires
            this.initPairContract();
            
            this.initialized = true;
            this._connectAttempts = 0;
            
            Logger.log("web3", "Wallet connected successfully", { account: this.account });
            return { success: true, account: this.account };
            
        } catch (error) {
            this.lastError = error;
            Logger.error(error, { context: 'connectWallet', attempts: this._connectAttempts });
            
            // Réinitialisation si trop d'échecs
            if (this._connectAttempts >= 3) {
                this.cleanup();
                this._connectAttempts = 0;
            }
            
            return { success: false, account: null, error };
        }
    }

    /**
     * Déconnexion du wallet
     */
    async disconnectWallet() {
        try {
            Logger.log("web3", "Disconnecting wallet", { account: this.account });
            
            // Réinitialisation complète
            this.cleanup();
            
            // Suppression des données localStorage si besoin
            try {
                if (window.localStorage) {
                    // Supprimer les clés de connexion persistante si existantes
                    window.localStorage.removeItem('walletconnect');
                    window.localStorage.removeItem('WALLETCONNECT_DEEPLINK_CHOICE');
                    window.localStorage.removeItem('connectedProvider');
                    window.localStorage.removeItem('connectedAccount');
                }
            } catch (e) {
                // Ignorer les erreurs localStorage
            }
            
            Logger.log("web3", "Wallet disconnected");
            return true;
        } catch (error) {
            Logger.error(error, { context: 'disconnectWallet' });
            return false;
        }
    }

    /**
     * Changement de réseau
     */
    async switchNetwork() {
        try {
            if (!window.ethereum) throw new Error('No Ethereum provider');
            
            Logger.log("web3", "Switching network");
            
            await window.ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: `0x${NETWORK_CONFIG.CHAIN_ID.toString(16)}` }]
            });
            
            // Mise à jour du réseau
            if (this.provider) {
                const network = await this.provider.getNetwork();
                this.chainId = network.chainId;
            }
            
            Logger.log("web3", "Network switched successfully", { chainId: this.chainId });
            return true;
        } catch (error) {
            Logger.error(error, { context: 'switchNetwork' });
            throw new Error('Please switch to Ethereum Mainnet');
        }
    }

    /**
     * Monitoring d'une transaction
     * @param {string} txHash Hash de la transaction
     * @param {Object} callbacks Callbacks (onConfirm, onError)
     * @returns {Promise<Object>} Receipt
     */
    async monitorTransaction(txHash, callbacks = {}) {
        const { onConfirm, onError } = callbacks;
        
        try {
            if (!this.provider) {
                await this.initialize();
                if (!this.provider) throw new Error('Provider not initialized');
            }
            
            Logger.log("transaction", "Monitoring transaction", { txHash });
            
            const receipt = await this.provider.waitForTransaction(
                txHash, 
                NETWORK_CONFIG.CONFIRMATIONS_REQUIRED
            );
            
            if (receipt.status === 1) {
                Logger.log("transaction", "Transaction confirmed", { 
                    txHash, 
                    blockNumber: receipt.blockNumber 
                });
                
                onConfirm && await onConfirm(receipt);
                return receipt;
            } else {
                throw new Error('Transaction failed');
            }
        } catch (error) {
            Logger.error(error, { context: 'monitorTransaction', txHash });
            onError && onError(error);
            throw this.formatError(error);
        }
    }

    /**
     * Initialisation du contrat de pair Uniswap
     */
    initPairContract() {
        if (!this.provider || !UNISWAP_PAIR_ADDRESS) {
            Logger.log("web3", "Cannot initialize pair contract: missing provider or address");
            return null;
        }
        
        try {
            Logger.log("web3", "Initializing pair contract");
            
            const pairAbi = [
                'function getReserves() view returns (uint112, uint112, uint32)',
                'function token0() view returns (address)',
                'function token1() view returns (address)',
                'function balanceOf(address) view returns (uint256)',
                'function totalSupply() view returns (uint256)'
            ];
            
            this.pairContract = new ethers.Contract(
                UNISWAP_PAIR_ADDRESS,
                pairAbi,
                this.provider
            );
            
            Logger.log("web3", "Pair contract initialized");
            return this.pairContract;
        } catch (error) {
            Logger.error(error, { context: 'initPairContract' });
            return null;
        }
    }

    /**
 * Récupération des informations de liquidité
 * @param {string} account Adresse du compte
 * @returns {Promise<Object>} Informations de liquidité
 */
async getLiquidityInfo(account) {
    try {
        Logger.log("web3", "Getting liquidity info", { account });
        
        // Valeurs par défaut en cas d'erreur
        const defaultResult = {
            tokenReserve: '0',
            ethReserve: '0',
            lpBalance: '0',
            poolShare: '0',
            totalSupply: '0',
            totalLiquidity: '0'
        };
        
        // Vérifications de base
        if (!this.provider || !account) {
            Logger.log("web3", "Missing provider or account for liquidity info");
            return defaultResult;
        }
        
        // Initialisation du contrat de pair si nécessaire
        if (!this.pairContract) {
            this.initPairContract();
            
            if (!this.pairContract) {
                Logger.log("web3", "Failed to initialize pair contract");
                return defaultResult;
            }
        }
        
        // Récupération des données en parallèle pour optimiser
        const [token0Promise, reservesPromise, lpBalancePromise, totalSupplyPromise] = [
            this.pairContract.token0().catch(() => WETH_ADDRESS),
            this.pairContract.getReserves().catch(() => [ethers.BigNumber.from(0), ethers.BigNumber.from(0), 0]),
            this.pairContract.balanceOf(account).catch(() => ethers.BigNumber.from(0)),
            this.pairContract.totalSupply().catch(() => ethers.BigNumber.from(0))
        ];
        
        // AMÉLIORÉ: Récupération des LP stakés avec méthodes multiples
let stakedLPAmount = ethers.BigNumber.from(0);
try {
    // Méthode 1: Appel direct à lpStakes
    if (this.contract && typeof this.contract.lpStakes === 'function') {
        const lpStakeInfo = await this.contract.lpStakes(account);
        
        if (lpStakeInfo && lpStakeInfo.amount) {
            // Si lpStakes retourne un objet
            stakedLPAmount = lpStakeInfo.amount;
            Logger.log("web3", "LP stakes retrieved from contract (object)", { 
                stakedLP: ethers.utils.formatEther(stakedLPAmount) 
            });
        } else if (Array.isArray(lpStakeInfo) && lpStakeInfo.length > 0) {
            // Si lpStakes retourne un array (mapping structure)
            stakedLPAmount = lpStakeInfo[0]; // Premier élément est amount
            Logger.log("web3", "LP stakes retrieved from contract (array)", { 
                stakedLP: ethers.utils.formatEther(stakedLPAmount) 
            });
        }
    }
    
    // Si stakedLPAmount est toujours zéro, essayer méthode 2: le service robuste
    if (stakedLPAmount.isZero()) {
        const stakedLPAmountStr = await this.getLPStakedAmountRobust(account);
        if (stakedLPAmountStr && parseFloat(stakedLPAmountStr) > 0) {
            stakedLPAmount = ethers.utils.parseEther(stakedLPAmountStr);
            Logger.log("web3", "LP stakes retrieved using robust method", { 
                stakedLP: ethers.utils.formatEther(stakedLPAmount) 
            });
        }
    }
    
    // Si toujours zéro, essayer méthode 3: les événements (méthode originale)
    if (stakedLPAmount.isZero()) {
        try {
            const stakingContract = this.contract;
            if (stakingContract) {
                // Tenter d'utiliser les événements LP staking comme fallback
                const filter = stakingContract.filters.LPStaked(account);
                const stakeEvents = await stakingContract.queryFilter(filter);
                
                const unstakeFilter = stakingContract.filters.LPUnstaked(account);
                const unstakeEvents = await stakingContract.queryFilter(unstakeFilter);
                
                let stakeTotal = ethers.BigNumber.from(0);
                let unstakeTotal = ethers.BigNumber.from(0);
                
                for (const event of stakeEvents) {
                    stakeTotal = stakeTotal.add(event.args.amount);
                }
                
                for (const event of unstakeEvents) {
                    unstakeTotal = unstakeTotal.add(event.args.amount);
                }
                
                // Calculer le net total (ne pas aller en négatif)
                if (stakeTotal.gt(unstakeTotal)) {
                    stakedLPAmount = stakeTotal.sub(unstakeTotal);
                } else {
                    stakedLPAmount = ethers.BigNumber.from(0);
                }
                
                Logger.log("web3", "LP stakes calculated from events", { 
                    stakedLP: ethers.utils.formatEther(stakedLPAmount) 
                });
            }
        } catch (eventErr) {
            Logger.error(eventErr, { context: 'getLiquidityInfo.events' });
        }
    }
    
    // Méthode 4: Vérifier localStorage comme dernier recours
    if (stakedLPAmount.isZero()) {
        try {
            const cacheKey = `lp_amount_${account.toLowerCase()}`;
            const cachedAmount = localStorage.getItem(cacheKey);
            if (cachedAmount && parseFloat(cachedAmount) > 0) {
                stakedLPAmount = ethers.utils.parseEther(cachedAmount);
                Logger.log("web3", "Using cached LP amount from localStorage", {
                    stakedLP: cachedAmount
                });
            }
        } catch (cacheErr) {
            // Ignorer les erreurs localStorage
        }
    }
} catch (err) {
    Logger.error(err, { context: 'getLiquidityInfo.stakedLP', address });
}

// Sauvegarder dans localStorage pour les futurs accès
if (!stakedLPAmount.isZero()) {
    try {
        const cacheKey = `lp_amount_${account.toLowerCase()}`;
        localStorage.setItem(cacheKey, ethers.utils.formatEther(stakedLPAmount));
    } catch (e) {
        // Ignorer les erreurs localStorage
    }
}
        
        // Attendre toutes les promesses
        const [token0, reserves, lpBalance, totalSupply] = await Promise.all([
            token0Promise, reservesPromise, lpBalancePromise, totalSupplyPromise
        ]);
        
        // Détermination de l'ordre des tokens
        const isToken0 = token0.toLowerCase() === PROXY_ADDRESS.toLowerCase();
        
        // Calcul des valeurs
        const tokenReserve = isToken0 ? reserves[0] : reserves[1];
        const ethReserve = isToken0 ? reserves[1] : reserves[0];
        
        // Calcul du total LP possédé (wallet + staked)
        const totalLPOwned = lpBalance.add(stakedLPAmount);

        // DEBUG POOL SHARE
        console.log("DIAGNOSTIC LP BALANCE:", {
            account,
            lpBalance: ethers.utils.formatEther(lpBalance),
            stakedLPAmount: ethers.utils.formatEther(stakedLPAmount),
            totalLPOwned: ethers.utils.formatEther(totalLPOwned),
            totalSupply: ethers.utils.formatEther(totalSupply),
            ratioRaw: totalLPOwned.toString() + '/' + totalSupply.toString()
        });

        // Calcul du pool share avec haute précision
        let poolShare = '0';
        if (!totalSupply.isZero() && !totalLPOwned.isZero()) {
            try {
                // Utiliser une très haute précision pour les petits pourcentages
                const precisionFactor = ethers.BigNumber.from(10).pow(18); // 10^18 pour précision maximale
                const rawShare = totalLPOwned.mul(precisionFactor).div(totalSupply);
                
                // Convertir en pourcentage avec une haute précision
                const percentageFactor = ethers.BigNumber.from(100); // Pour le pourcentage standard
                const sharePercentage = rawShare.mul(percentageFactor);
                
                // CORRECTION: Division par 10^18 (au lieu de 10^16) pour obtenir le pourcentage correct
                poolShare = sharePercentage.toString() / 10**18;
                poolShare = poolShare.toFixed(4); // Formatage avec 4 décimales
                
                console.log("CALCULATED POOL SHARE:", {
                    account,
                    rawShare: rawShare.toString(),
                    sharePercentage: sharePercentage.toString(),
                    finalPoolShare: poolShare
                });
            } catch (error) {
                console.error("Error calculating pool share:", error);
                // Fallback simple
                poolShare = (parseFloat(ethers.utils.formatEther(totalLPOwned)) / 
                            parseFloat(ethers.utils.formatEther(totalSupply)) * 100).toFixed(4);
            }
        } else {
            console.log("Zero values detected:", {
                lpBalanceIsZero: totalLPOwned.isZero(),
                totalSupplyIsZero: totalSupply.isZero()
            });
        }

        // AJOUT: Calcul correct de la liquidité totale en USD
        // Récupérer le prix ETH de manière fiable
        const ethPrice = await this._getEthPrice();
        
        // Conversion des valeurs BigNumber en nombres pour le calcul
        const gr33dReserveNum = parseFloat(ethers.utils.formatEther(tokenReserve));
        const ethReserveNum = parseFloat(ethers.utils.formatEther(ethReserve));
        
        // Calcul du prix GR33D en USD
        const gr33dPriceUSD = gr33dReserveNum > 0 ? (ethReserveNum * ethPrice) / gr33dReserveNum : 0;
        
        // Calcul de la liquidité selon formule Uniswap V2 précise
        // Dans Uniswap V2, la liquidité est 2 * sqrt(x * y * px * py)
        // où x et y sont les réserves et px, py les prix
        const totalLiquidityUSD = 2 * Math.sqrt(gr33dReserveNum * ethReserveNum * gr33dPriceUSD * ethPrice);
        
        // Log pour débugger
        console.log("LIQUIDITY CALCULATION:", {
            gr33dReserveNum,
            ethReserveNum,
            ethPrice,
            gr33dPriceUSD,
            totalLiquidityUSD
        });

        // CORRECTION: S'assurer que toutes les valeurs BigNumber sont converties en string
        const result = {
            tokenReserve: ethers.utils.formatEther(tokenReserve),
            ethReserve: ethers.utils.formatEther(ethReserve),
            lpBalance: ethers.utils.formatEther(lpBalance),
            stakedLPAmount: ethers.utils.formatEther(stakedLPAmount),
            totalLPOwned: ethers.utils.formatEther(totalLPOwned),
            poolShare: poolShare,
            totalSupply: ethers.utils.formatEther(totalSupply),
            totalLiquidity: totalLiquidityUSD.toString()
        };
        
        Logger.log("web3", "Liquidity info retrieved", result);
        return result;
        
    } catch (error) {
        Logger.error(error, { context: 'getLiquidityInfo', account });
        // Retour défensif en cas d'erreur
        return {
            tokenReserve: '0',
            ethReserve: '0',
            lpBalance: '0',
            stakedLPAmount: '0',
            totalLPOwned: '0',
            poolShare: '0',
            totalSupply: '0',
            totalLiquidity: '0'
        };
    }
}

    /**
     * Récupération du prix du token
     * @returns {Promise<string>} Prix en USD
     */
    async getTokenPrice() {
        try {
            Logger.log("web3", "Getting token price");
            
            // Initialisation si nécessaire
            if (!this.pairContract) {
                this.initPairContract();
                
                if (!this.pairContract) {
                    Logger.log("web3", "Failed to initialize pair contract for price check");
                    return '0';
                }
            }
            
            // Récupération des données du contrat
            const token0Promise = this.pairContract.token0().catch(() => WETH_ADDRESS);
            const reservesPromise = this.pairContract.getReserves().catch(() => [
                ethers.BigNumber.from(0), 
                ethers.BigNumber.from(0), 
                0
            ]);
            
            const [token0, reserves] = await Promise.all([token0Promise, reservesPromise]);
            
            // Détermination de l'ordre des tokens
            const isToken0 = token0.toLowerCase() === PROXY_ADDRESS.toLowerCase();
            
            // Calcul des valeurs
            const tokenReserve = isToken0 ? reserves[0] : reserves[1];
            const ethReserve = isToken0 ? reserves[1] : reserves[0];
            
            if (tokenReserve.isZero()) return '0';
            
            // Calcul du prix en ETH
            const priceInEth = ethReserve.mul(ethers.constants.WeiPerEther).div(tokenReserve);
            
            // Récupération du prix ETH en USD
            const ethPriceUsd = await this._getEthPrice();
            
            // Calcul du prix du token en USD
            const tokenPriceUsd = Number(ethers.utils.formatEther(priceInEth)) * ethPriceUsd;
            
            Logger.log("web3", "Token price calculated", { 
                price: tokenPriceUsd, 
                ethPrice: ethPriceUsd 
            });
            
            // CORRECTION: Toujours retourner un string
            return tokenPriceUsd.toString();
        } catch (error) {
            Logger.error(error, { context: 'getTokenPrice' });
            return '0';
        }
    }

    /**
     * Récupération du prix ETH
     * @returns {Promise<number>} Prix ETH en USD
     */
    async _getEthPrice() {
        try {
            Logger.log("web3", "Getting ETH price");
            
            // Essai avec l'API interne
            try {
                const response = await fetch('/api/prices', {
                    headers: { 'Cache-Control': 'max-age=30' }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data?.eth?.usd) return parseFloat(data.eth.usd);
                    if (data?.eth?.coingecko) return parseFloat(data.eth.coingecko);
                    if (data?.eth?.cryptocompare) return parseFloat(data.eth.cryptocompare);
                }
            } catch (apiError) {
                Logger.warn("web3", "API price fetch failed, trying fallback");
            }
            
            // Fallback CoinGecko
            try {
                const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
                if (response.ok) {
                    const data = await response.json();
                    if (data.ethereum?.usd) {
                        return parseFloat(data.ethereum.usd);
                    }
                }
            } catch (geckoError) {
                Logger.warn("web3", "CoinGecko price fetch failed, trying next fallback");
            }
            
            // Fallback Binance
            try {
                const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=ETHUSDT');
                if (response.ok) {
                    const data = await response.json();
                    if (data.price) {
                        return parseFloat(data.price);
                    }
                }
            } catch (binanceError) {
                Logger.warn("web3", "Binance price fetch failed, using default");
            }
            
            // Dernière solution: valeur approximative
            return 3000;
        } catch (error) {
            Logger.error(error, { context: '_getEthPrice' });
            return 3000; // Valeur par défaut en cas d'erreur
        }
    }

    /**
 * Appel sécurisé d'une méthode de contrat
 * @param {string} method Méthode du contrat
 * @param {any} fallbackValue Valeur par défaut en cas d'erreur
 * @param  {...any} args Arguments à passer à la méthode
 * @returns {Promise<any>} Résultat de l'appel
 */
async safeContractCall(method, fallbackValue, ...args) {
    try {
        if (!this.contract) {
            Logger.warn("web3", `Contract not initialized for call to ${method}`);
            return fallbackValue;
        }
        
        if (typeof this.contract[method] !== 'function') {
            Logger.warn("web3", `Method ${method} not available on contract`);
            return fallbackValue;
        }
        
        try {
            // Premier essai avec le provider principal
            const result = await this.contract[method](...args);
            
            // Formatage automatique des BigNumber si nécessaire
            if (result && result._isBigNumber) {
                return ethers.utils.formatEther(result);
            }
            
            return result;
        } catch (primaryError) {
            // Si l'appel échoue, essayer avec un RPC alternatif
            Logger.warn("web3", `Primary call to ${method} failed, trying alternative RPC`, {
                error: primaryError.message
            });
            
            // Obtenir un provider alternatif
            const altProvider = await this.getAlternativeProvider();
            if (!altProvider) {
                Logger.warn("web3", "No alternative provider available, returning fallback value");
                return fallbackValue;
            }
            
            // Créer un nouveau contrat avec le provider alternatif
            const altContract = new ethers.Contract(
                PROXY_ADDRESS,
                CONTRACT_ABI,
                altProvider
            );
            
            const result = await altContract[method](...args);
            
            // Formatage automatique des BigNumber si nécessaire
            if (result && result._isBigNumber) {
                return ethers.utils.formatEther(result);
            }
            
            return result;
        }
    } catch (error) {
        Logger.error(error, { context: 'safeContractCall', method, args });
        return fallbackValue;
    }
}

    /**
     * Formatage des erreurs
     * @param {Error|string} error Erreur à formater
     * @returns {Error} Erreur formatée
     */
    formatError(error) {
        // Extraction du message d'erreur
        const errorMessage = error.message || error.toString();
        
        // Conversion des erreurs techniques en messages plus clairs
        if (errorMessage.includes('user rejected')) {
            return new Error('Transaction rejected by user');
        }
        
        if (errorMessage.includes('insufficient funds')) {
            return new Error('Insufficient ETH balance for gas');
        }
        
        if (errorMessage.includes('network') || errorMessage.includes('chain')) {
            return new Error(ERROR_MESSAGES.UNSUPPORTED_NETWORK);
        }
        
        if (errorMessage.includes('blacklisted')) {
            return new Error(ERROR_MESSAGES.ADDRESS_BLACKLISTED);
        }
        
        if (errorMessage.includes('Max burn reached')) {
            return new Error(ERROR_MESSAGES.MAX_BURN_REACHED);
        }
        
        if (errorMessage.includes('exceeds')) {
            return new Error(ERROR_MESSAGES.EXCEEDS_TX_LIMIT);
        }
        
        if (errorMessage.includes('Still locked')) {
            return new Error(ERROR_MESSAGES.STILL_LOCKED);
        }
        
        if (errorMessage.includes('Trading not enabled')) {
            return new Error(ERROR_MESSAGES.TRADING_DISABLED);
        }
        
        // Erreur générique
        return new Error(error.reason || errorMessage);
    }

    /**
     * Nettoyage de l'état
     */
    cleanup() {
        this.provider = null;
        this.signer = null;
        this.contract = null;
        this.pairContract = null;
        this.account = null;
        this.chainId = null;
        this.initialized = false;
        
        Logger.log("web3", "Web3 service state cleaned up");
    }

    /**
     * Vérification que le contrat est initialisé et que l'utilisateur est connecté
     * @returns {boolean} État de connexion
     */
    isConnected() {
        return this.initialized && !!this.account && !!this.contract;
    }

/**
 * Obtient un provider alternatif en cas d'échec du principal
 * @returns {Promise<ethers.providers.JsonRpcProvider|null>} Provider alternatif ou null
 */
async getAlternativeProvider() {
    try {
        const rpcs = NETWORK_CONFIG.FALLBACK_RPC_URLS || [];
        if (rpcs.length === 0) return null;
        
        // Trouver le meilleur RPC (le moins d'échecs récents)
        let bestRpcIndex = 0;
        let lowestFailCount = Infinity;
        
        for (let i = 0; i < rpcs.length; i++) {
            const failCount = this.rpcStatus.failures.get(i) || 0;
            if (failCount < lowestFailCount) {
                lowestFailCount = failCount;
                bestRpcIndex = i;
            }
        }
        
        // Mémoriser le RPC choisi
        this.rpcStatus.current = bestRpcIndex;
        this.rpcStatus.lastSwitch = Date.now();
        
        const provider = new ethers.providers.JsonRpcProvider(
            rpcs[bestRpcIndex],
            {chainId: NETWORK_CONFIG.CHAIN_ID}
        );
        
        // Tester le provider
        await provider.getBlockNumber();
        Logger.log("web3", `Using alternative RPC #${bestRpcIndex + 1}`, { 
            url: rpcs[bestRpcIndex].split('/').slice(0, 3).join('/') 
        });
        
        return provider;
    } catch (error) {
        // Marquer cet RPC comme ayant échoué
        this.rpcStatus.failures.set(this.rpcStatus.current, 
            (this.rpcStatus.failures.get(this.rpcStatus.current) || 0) + 1);
            
        Logger.error(error, { context: 'getAlternativeProvider' });
        return null;
    }
}

/**
 * Méthode robuste pour récupérer le montant de LP staké
 * Essaie plusieurs méthodes avec fallbacks
 * @param {string} address Adresse du wallet
 * @returns {Promise<string>} Montant staké formaté
 */
async getLPStakedAmountRobust(address) {
    try {
        if (!address) return '0';
        
        const contract = this.contract;
        if (!contract) return '0';
        
        // Tableau pour suivre toutes les tentatives de récupération
        const results = [];
        
        // Méthode 1: Appel direct à lpStakes (méthode principale)
        try {
            console.log("Tentative méthode 1: lpStakes directe");
            const lpStake = await Promise.race([
                contract.lpStakes(address),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('lpStakes timeout')), 5000)
                )
            ]);
            
            if (lpStake && lpStake.amount) {
                const amount = ethers.utils.formatEther(lpStake.amount);
                console.log(`✓ Méthode 1 réussie: ${amount} LP`);
                results.push({ method: 'lpStakes', amount, success: true });
                return amount; // Retourner immédiatement si succès
            } else if (Array.isArray(lpStake) && lpStake.length > 0) {
                // Pour les contrats qui retournent un array au lieu d'un objet
                const amount = ethers.utils.formatEther(lpStake[0]);
                console.log(`✓ Méthode 1 réussie (format array): ${amount} LP`);
                results.push({ method: 'lpStakes-array', amount, success: true });
                return amount;
            }
        } catch (e) {
            console.warn("✗ Méthode 1 (lpStakes) échouée:", e.message);
            results.push({ method: 'lpStakes', success: false, error: e.message });
        }
        
        // Méthode 2: Utilisation des événements (fallback ultime)
        try {
            console.log("Tentative méthode 2: événements LP");
            // Chercher tous les événements LPStaked pour cette adresse
            const stakeFilter = contract.filters.LPStaked(address);
            const stakeEvents = await contract.queryFilter(stakeFilter);
            
            // Chercher tous les événements LPUnstaked pour cette adresse
            const unstakeFilter = contract.filters.LPUnstaked(address);
            const unstakeEvents = await contract.queryFilter(unstakeFilter);
            
            console.log(`Trouvé ${stakeEvents.length} événements LPStaked et ${unstakeEvents.length} événements LPUnstaked`);
            
            // Calculer le total (stakes - unstakes)
            let total = ethers.BigNumber.from(0);
            
            for (const event of stakeEvents) {
                total = total.add(event.args.amount);
            }
            
            for (const event of unstakeEvents) {
                total = total.sub(event.args.amount);
            }
            
            const formattedTotal = ethers.utils.formatEther(total);
            console.log(`✓ Méthode 2 réussie: ${formattedTotal} LP (calculé via événements)`);
            results.push({ method: 'events', amount: formattedTotal, success: true });
            return formattedTotal;
        } catch (e) {
            console.warn("✗ Méthode 2 (événements) échouée:", e.message);
            results.push({ method: 'events', success: false, error: e.message });
        }
        
        // Si tout a échoué, consulter le cache local
        const cacheKey = `lp_amount_${address.toLowerCase()}`;
        const cachedAmount = localStorage.getItem(cacheKey);
        if (cachedAmount) {
            console.log(`✓ Utilisation du cache local: ${cachedAmount} LP`);
            return cachedAmount;
        }
        
        console.error("Toutes les méthodes de récupération LP ont échoué:", results);
        return '0';
    } catch (error) {
        console.error("Erreur récupération LP stakés:", error);
        return '0';
    }
}
} // <-- CETTE ACCOLADE FERMANTE MANQUAIT ICI (ferme la classe Web3Service)

// Instanciation du service
const web3service = new Web3Service();

// Initialisation au chargement pour vérifier un wallet déjà connecté
if (typeof window !== 'undefined') {
    web3service.initialize().catch(() => {
        // Silently fail - l'utilisateur peut connecter manuellement
    });
}

export default web3service;
